/**
 * 端到端加密 SDK（Sync E2EE）
 *
 * 算法：
 *   密钥派生  — PBKDF2-SHA256，600 000 次迭代（OWASP 2023 推荐下限）
 *   对称加密  — AES-256-GCM（Web Crypto API 原生，浏览器零依赖）
 *   认证标签  — 128-bit（AES-GCM 默认，不可配置）
 *
 * 协议：
 *   用户输入口令 → PBKDF2(password, salt, 600k) → AES-256 key
 *   加密：JSON.stringify(data) → TextEncoder → AES-GCM(key, iv) → base64
 *   解密：base64 → AES-GCM(key, iv) → TextDecoder → JSON.parse
 *
 * 安全边界：
 *   - 口令从不离开浏览器；服务端只存 salt + iv + 密文
 *   - 每次加密使用新随机 iv（12 字节），同一明文两次密文不同
 *   - AES-GCM 自带完整性校验（tag），篡改密文会抛异常
 *   - 服务端无法还原明文（无口令、无 key）
 */

// ─── 常量 ──────────────────────────────────────────────────────────────────────
const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12; // AES-GCM 推荐 96-bit nonce
const KEY_BITS = 256;

// ─── 工具 ──────────────────────────────────────────────────────────────────────

/** 随机字节 → hex 字符串（服务端存储友好） */
const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

/** hex 字符串 → Uint8Array */
const hexToBytes = (hex: string): Uint8Array => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
};

/** ArrayBuffer → base64（兼容浏览器） */
const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/** base64 → ArrayBuffer */
const base64ToBuffer = (b64: string): ArrayBuffer => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// ─── 密钥派生 ──────────────────────────────────────────────────────────────────

/**
 * 生成随机 salt（16 字节 hex）
 * 新注册用户由前端生成，存到服务端 sync_snapshots.salt 列
 */
export const generateSalt = (): string => bytesToHex(crypto.getRandomValues(new Uint8Array(SALT_BYTES)));

/**
 * 从口令 + salt 派生 AES-256 key
 *
 * @param password 用户输入的同步口令（明文，UTF-8）
 * @param salt hex 字符串（16 字节 = 32 字符）
 * @returns 不可导出的 CryptoKey（AES-GCM 256-bit）
 */
export const deriveKey = async (password: string, salt: string): Promise<CryptoKey> => {
  const enc = new TextEncoder();
  const passwordBuffer = enc.encode(password);
  const saltBuffer = hexToBytes(salt);

  // 导入口令为原始 key material（不可直接加密，仅用于派生）
  const baseKey = await crypto.subtle.importKey('raw', passwordBuffer, 'PBKDF2', false, ['deriveKey']);

  // PBKDF2 派生 AES-256 key
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    false, // 不可导出（安全：内存中的 key 无法被读走）
    ['encrypt', 'decrypt'],
  );
};

// ─── 加密/解密 ─────────────────────────────────────────────────────────────────

/** 加密结果：iv（hex）+ 密文（base64）+ aad（可选，hex） */
export interface EncryptedPayload {
  iv: string; // 12 字节随机 IV（hex，24 字符）
  ciphertext: string; // AES-GCM 密文（base64，含 16 字节 auth tag）
  aad?: string; // 可选 AAD（hex），绑定上下文防止跨用户/跨字段密文替换
}

/**
 * AES-256-GCM 加密
 *
 * @param data 任意 JS 对象（会 JSON.stringify 后加密）
 * @param key 由 deriveKey 派生的 CryptoKey
 * @param aadContext 可选 AAD 上下文（绑定 user_id / entity_type 等，防密文替换攻击）
 * @returns { iv, ciphertext, aad? } 均为基础字符串，可直接存服务端
 */
export const encrypt = async (
  data: unknown,
  key: CryptoKey,
  aadContext?: string,
): Promise<EncryptedPayload> => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const enc = new TextEncoder();
  const plaintext = enc.encode(JSON.stringify(data));

  const encryptParams = {
    name: 'AES-GCM' as const,
    iv,
    ...(aadContext && { additionalData: enc.encode(aadContext) }),
  };

  const ciphertextBuffer = await crypto.subtle.encrypt(
    encryptParams,
    key,
    plaintext,
  );

  const result: EncryptedPayload = {
    iv: bytesToHex(iv),
    ciphertext: bufferToBase64(ciphertextBuffer),
  };
  if (aadContext) {
    result.aad = bytesToHex(enc.encode(aadContext));
  }
  return result;
};

/**
 * AES-256-GCM 解密
 *
 * @param payload { iv, ciphertext, aad? }
 * @param key 由 deriveKey 派生的 CryptoKey
 * @param expectedAad 可选期望 AAD（若 payload 带 aad 字段则自动校验）
 * @returns 解密后的 JS 对象（JSON.parse 后的结果）
 * @throws 口令错误、密文被篡改、或 AAD 不匹配时抛异常（AES-GCM tag 校验失败）
 */
export const decrypt = async <T = unknown>(
  payload: EncryptedPayload,
  key: CryptoKey,
  expectedAad?: string,
): Promise<T> => {
  const iv = hexToBytes(payload.iv);
  const ciphertext = base64ToBuffer(payload.ciphertext);
  const dec = new TextDecoder();

  // AAD 来源：显式传入 > payload 自带 > 无
  const aadHex = expectedAad ? bytesToHex(new TextEncoder().encode(expectedAad)) : payload.aad;
  const additionalData = aadHex ? hexToBytes(aadHex) : undefined;

  const decryptParams = {
    name: 'AES-GCM' as const,
    iv,
    ...(additionalData && { additionalData }),
  };

  const plaintextBuffer = await crypto.subtle.decrypt(
    decryptParams,
    key,
    ciphertext,
  );

  const plaintext = dec.decode(plaintextBuffer);
  return JSON.parse(plaintext) as T;
};

// ─── 便捷包装（用于一次性备份/恢复场景） ────────────────────────────────────────

// 默认 AAD 上下文：绑定"Molore 备份"用途，防止密文被挪作他用
const DEFAULT_AAD_CONTEXT = 'psb-backup:v1';

/**
 * 加密完整备份包（含元信息）
 *
 * @param data 要加密的数据对象
 * @param password 用户口令
 * @param salt 服务端存储的 salt（若为空则新生成）
 * @param aadContext 可选 AAD 上下文（默认 'psb-backup:v1'，绑定用途防替换）
 * @returns { salt, iv, ciphertext, aad? } — 字段一起存服务端
 */
export const encryptBackup = async (
  data: unknown,
  password: string,
  salt?: string,
  aadContext: string = DEFAULT_AAD_CONTEXT,
): Promise<{ salt: string; iv: string; ciphertext: string; aad?: string }> => {
  const finalSalt = salt || generateSalt();
  const key = await deriveKey(password, finalSalt);
  const payload = await encrypt(data, key, aadContext);
  return { salt: finalSalt, ...payload };
};

/**
 * 解密备份包
 *
 * @param payload { salt, iv, ciphertext, aad? }
 * @param password 用户口令
 * @param expectedAad 可选期望 AAD（默认从 payload.aad 读取）
 * @returns 解密后的数据对象
 * @throws 口令错误、密文损坏、或 AAD 不匹配时抛异常
 */
export const decryptBackup = async <T = unknown>(
  payload: { salt: string; iv: string; ciphertext: string; aad?: string },
  password: string,
  expectedAad?: string,
): Promise<T> => {
  const key = await deriveKey(password, payload.salt);
  return decrypt<T>({ iv: payload.iv, ciphertext: payload.ciphertext, aad: payload.aad }, key, expectedAad);
};

// ─── 口令强度检查 ──────────────────────────────────────────────────────────────

/**
 * 简单口令强度评估（前端 UX 提示用，非安全策略）
 * @returns 'weak' | 'medium' | 'strong'
 */
export const assessPasswordStrength = (
  password: string,
): 'weak' | 'medium' | 'strong' => {
  if (password.length < 8) return 'weak';
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const score = [hasUpper, hasLower, hasDigit, hasSymbol].filter(Boolean).length;
  if (password.length >= 12 && score >= 3) return 'strong';
  if (password.length >= 10 && score >= 2) return 'medium';
  return score >= 2 ? 'medium' : 'weak';
};
